import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Globe, ExternalLink, Link2, AlertTriangle, Loader2, FileText, TrendingUp } from 'lucide-react';
import { SeoMetricsPanel } from './SeoMetricsPanel';
import { getSensitiveTopicLabel } from '../../utils/sensitiveTopicsTranslations';
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default sensitive category names (fallback if not provided from backend)
const DEFAULT_SENSITIVE_CATEGORY_NAMES = [
  'Casino', 'Apuestas', 'CBD', 'Criptomonedas', 'Adultos', 'Erótico', 'VPN', 
  'Tarot', 'Cannabis', 'Forex', 'Trading', 'Préstamos', 'Dating', 'Gambling',
  'Cryptocurrency', 'Sex', 'Erotic', 'Porn', 'Adult'
];

// Parse categories into regular and sensitive
const parseCategories = (categoryString, sensitiveCategoryNames = []) => {
  if (!categoryString) return { regular: [], sensitive: [] };
  const cats = categoryString.split(',').map(c => c.trim()).filter(c => c);
  const regular = [];
  const sensitive = [];
  
  // Combine default names with dynamic ones from backend (normalized to lowercase for comparison)
  const allSensitiveNamesLower = [
    ...DEFAULT_SENSITIVE_CATEGORY_NAMES.map(s => s.toLowerCase()),
    ...sensitiveCategoryNames.map(s => s.toLowerCase())
  ];
  
  cats.forEach(cat => {
    const catLower = cat.toLowerCase();
    // Check for exact match (case-insensitive) OR if cat contains a sensitive name
    const isSensitive = allSensitiveNamesLower.some(s => 
      catLower === s || catLower.includes(s) || s.includes(catLower)
    );
    if (isSensitive) {
      sensitive.push(cat);
    } else {
      regular.push(cat);
    }
  });
  return { regular, sensitive };
};

// Country to flag - using flag CDN images
const getCountryFlag = (country) => {
  if (!country) return null;
  const countryUpper = country.toUpperCase().trim();
  const countryCodes = {
    'SPAIN': 'es', 'SPAIN (SPANISH)': 'es', 'ESPAÑA': 'es',
    'FRANCE': 'fr', 'FRANCE (FRENCH)': 'fr', 'FRANCIA': 'fr',
    'GERMANY': 'de', 'GERMANY (GERMAN)': 'de', 'ALEMANIA': 'de',
    'ITALY': 'it', 'ITALY (ITALIAN)': 'it', 'ITALIA': 'it',
    'PORTUGAL': 'pt', 'PORTUGAL (PORTUGUESE)': 'pt',
    'UK': 'gb', 'UNITED KINGDOM': 'gb', 'REINO UNIDO': 'gb', 'GB': 'gb', 'GREAT BRITAIN': 'gb',
    'USA': 'us', 'UNITED STATES': 'us', 'ESTADOS UNIDOS': 'us', 'US': 'us',
    'NETHERLANDS': 'nl', 'HOLANDA': 'nl', 'PAISES BAJOS': 'nl',
    'BELGIUM': 'be', 'BÉLGICA': 'be', 'BELGICA': 'be',
    'AUSTRIA': 'at',
    'SWITZERLAND': 'ch', 'SUIZA': 'ch',
    'POLAND': 'pl', 'POLONIA': 'pl',
    'CZECH REPUBLIC': 'cz', 'CZECHIA': 'cz', 'REPÚBLICA CHECA': 'cz',
    'SWEDEN': 'se', 'SUECIA': 'se',
    'NORWAY': 'no', 'NORUEGA': 'no',
    'DENMARK': 'dk', 'DINAMARCA': 'dk',
    'FINLAND': 'fi', 'FINLANDIA': 'fi',
    'IRELAND': 'ie', 'IRLANDA': 'ie',
    'GREECE': 'gr', 'GRECIA': 'gr',
    'ROMANIA': 'ro', 'RUMANIA': 'ro',
    'HUNGARY': 'hu', 'HUNGRÍA': 'hu', 'HUNGRIA': 'hu',
    'BULGARIA': 'bg',
    'CROATIA': 'hr', 'CROACIA': 'hr',
    'SLOVAKIA': 'sk', 'ESLOVAQUIA': 'sk',
    'SLOVENIA': 'si', 'ESLOVENIA': 'si',
    'LITHUANIA': 'lt', 'LITUANIA': 'lt',
    'LATVIA': 'lv', 'LETONIA': 'lv',
    'ESTONIA': 'ee',
    'CYPRUS': 'cy', 'CHIPRE': 'cy',
    'MALTA': 'mt',
    'LUXEMBOURG': 'lu', 'LUXEMBURGO': 'lu',
    'BRAZIL': 'br', 'BRASIL': 'br',
    'MEXICO': 'mx', 'MÉXICO': 'mx',
    'ARGENTINA': 'ar',
    'CHILE': 'cl',
    'COLOMBIA': 'co',
    'PERU': 'pe', 'PERÚ': 'pe',
    'INDIA': 'in',
    'CHINA': 'cn',
    'JAPAN': 'jp', 'JAPÓN': 'jp', 'JAPON': 'jp',
    'SOUTH KOREA': 'kr', 'COREA DEL SUR': 'kr',
    'AUSTRALIA': 'au',
    'CANADA': 'ca', 'CANADÁ': 'ca',
    'RUSSIA': 'ru', 'RUSIA': 'ru',
    'TURKEY': 'tr', 'TURQUÍA': 'tr', 'TURQUIA': 'tr',
    'INDONESIA': 'id',
    'THAILAND': 'th', 'TAILANDIA': 'th',
    'VIETNAM': 'vn',
    'MALAYSIA': 'my', 'MALASIA': 'my',
    'SINGAPORE': 'sg', 'SINGAPUR': 'sg',
    'PHILIPPINES': 'ph', 'FILIPINAS': 'ph',
    'EGYPT': 'eg', 'EGIPTO': 'eg',
    'SOUTH AFRICA': 'za', 'SUDÁFRICA': 'za', 'SUDAFRICA': 'za',
    'ISRAEL': 'il',
    'UNITED ARAB EMIRATES': 'ae', 'UAE': 'ae', 'EMIRATOS ÁRABES': 'ae',
    'SAUDI ARABIA': 'sa', 'ARABIA SAUDITA': 'sa',
    'UKRAINE': 'ua', 'UCRANIA': 'ua',
    'GLOBAL': null,
    'INTERNATIONAL': null,
  };
  const code = countryCodes[countryUpper];
  return code ? `https://flagcdn.com/24x18/${code}.png` : null;
};

// Parse ALL link types from the field and return array of badges
const getLinkTypeBadges = (linkType) => {
  if (!linkType || linkType === 'No') return [];
  const badges = [];
  const lower = linkType.toLowerCase();
  const parts = lower.split(',').map(p => p.trim());

  for (const part of parts) {
    // Check for follow/dofollow (but not no-follow/nofollow)
    if ((part === 'follow' || part.includes('dofollow')) && !part.includes('no-follow') && !part.includes('nofollow')) {
      badges.push({ label: 'DoFollow', color: 'bg-blue-100 text-blue-700' });
    }
    // Check for no-follow/nofollow
    if (part.includes('no-follow') || part.includes('nofollow')) {
      badges.push({ label: 'NoFollow', color: 'bg-slate-100 text-slate-600' });
    }
    if (part.includes('sponsor')) {
      badges.push({ label: 'Sponsored', color: 'bg-amber-100 text-amber-700' });
    }
    if (part.includes('ugc')) {
      badges.push({ label: 'UGC', color: 'bg-purple-100 text-purple-700' });
    }
  }

  return badges;
};

// Get sensitive categories that the product accepts
const getSensitiveCategories = (product, language) => {
  const categories = [];
  if (product.casino) {
    categories.push({ name: getSensitiveTopicLabel('casino', language), color: 'bg-red-100 text-red-700' });
  }
  if (product.cbd) {
    categories.push({ name: getSensitiveTopicLabel('cbd', language), color: 'bg-green-100 text-green-700' });
  }
  if (product.gambling) {
    categories.push({ name: getSensitiveTopicLabel('gambling', language), color: 'bg-orange-100 text-orange-700' });
  }
  if (product.cryptocurrency) {
    categories.push({ name: getSensitiveTopicLabel('cryptocurrency', language), color: 'bg-yellow-100 text-yellow-700' });
  }
  if (product.erotic) {
    categories.push({ name: getSensitiveTopicLabel('erotic', language), color: 'bg-pink-100 text-pink-700' });
  }
  if (product.sex) {
    categories.push({ name: getSensitiveTopicLabel('sex', language), color: 'bg-pink-100 text-pink-700' });
  }
  if (product.porn) {
    categories.push({ name: getSensitiveTopicLabel('porn', language), color: 'bg-pink-100 text-pink-700' });
  }
  if (product.vpn) {
    categories.push({ name: getSensitiveTopicLabel('vpn', language), color: 'bg-blue-100 text-blue-700' });
  }
  if (product.tarot) {
    categories.push({ name: getSensitiveTopicLabel('tarot', language), color: 'bg-purple-100 text-purple-700' });
  }
  if (product.cannabis) {
    categories.push({ name: getSensitiveTopicLabel('cannabis', language), color: 'bg-green-100 text-green-700' });
  }
  return categories;
};


const durationLabel = (d, es) => {
  if (!d) return '-';
  if (d.toLowerCase() === 'permanente') return es ? 'Permanente' : 'Permanent';
  return d;
};

const InfoCell = ({ label, children, className = 'bg-slate-50', testId }) => (
  <div className={`rounded-lg p-3 ${className}`} data-testid={testId}>
    <p className="text-xs text-muted-foreground mb-1">{label}</p>
    {children}
  </div>
);

const SimilarSiteCard = ({ s, matchedOn, es, onOpen }) => (
  <div className="group border border-slate-200 hover:border-violet-400 hover:bg-violet-50/40 transition-colors rounded-lg p-2.5" data-testid={`similar-site-${s.id}`}>
    <div className="flex items-center justify-between gap-2 mb-1">
      <button type="button" onClick={() => onOpen(s)} className="text-xs font-semibold text-slate-800 truncate hover:text-violet-700 text-left">
        {s.website}
      </button>
      <a href={s.website?.startsWith('http') ? s.website : `https://${s.website}`} target="_blank" rel="noopener noreferrer" className="text-violet-500 hover:text-violet-700 shrink-0">
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    </div>
    <button type="button" onClick={() => onOpen(s)} className="w-full text-left">
      <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
        <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-full">DR {s.dr || 0}</span>
        <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-full">{(s.organic_traffic || 0).toLocaleString('es-ES')} {es ? 'visitas/mes' : 'visits/mo'}</span>
        <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full font-semibold">{s.pvp || 0} €</span>
      </div>
      {matchedOn && (
        <p className="text-[10px] text-slate-500 mt-1">{es ? 'Coincide en' : 'Matches on'}: <span className="text-violet-700 font-medium">{matchedOn}</span></p>
      )}
    </button>
  </div>
);

export const ProductDetailDialog = ({
  product: initialProduct,
  open,
  onOpenChange,
  onFindSimilar,
  language = 'es',
  texts,
  sensitiveCategories = [],
}) => {
  const es = language === 'es';
  const [current, setCurrent] = useState(initialProduct);
  const [seo, setSeo] = useState({ metrics: null, history: [] });
  const [similarSites, setSimilarSites] = useState([]);
  const [similarLoading, setSimilarLoading] = useState(false);

  const contentRef = useRef(null);
  useEffect(() => { setCurrent(initialProduct); }, [initialProduct]);
  const product = current;
  useEffect(() => { if (contentRef.current) contentRef.current.scrollTop = 0; }, [product?.id]);

  // Full record (with weekly traffic history) for the SEO panel
  useEffect(() => {
    if (!open || !product?.id) return;
    let cancelled = false;
    setSeo({ metrics: product.seo_metrics || null, history: [] });
    axios.get(`${API}/products/${product.id}`)
      .then((r) => { if (!cancelled) setSeo({ metrics: r.data?.seo_metrics || null, history: r.data?.seo_traffic_history || [] }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, product?.id, product?.seo_metrics]);

  const firstCat = product?.category ? String(product.category).split(',')[0].trim() : '';

  useEffect(() => {
    if (!open || !product?.id) { setSimilarSites([]); return; }
    let cancelled = false;
    setSimilarLoading(true);
    const params = new URLSearchParams({ limit: '12', sort_by: 'dr', sort_order: 'desc' });
    if (product.country) params.append('country', product.country);
    if (firstCat) params.append('category', firstCat);
    if (product.dr) {
      params.append('dr_min', String(Math.max(0, product.dr - 15)));
      params.append('dr_max', String(product.dr + 15));
    }
    axios.get(`${API}/products?${params.toString()}`)
      .then((res) => { if (!cancelled) setSimilarSites((res.data?.products || []).filter((p) => p.id !== product.id).slice(0, 6)); })
      .catch(() => { if (!cancelled) setSimilarSites([]); })
      .finally(() => { if (!cancelled) setSimilarLoading(false); });
    return () => { cancelled = true; };
  }, [open, product?.id, product?.country, firstCat, product?.dr]);

  if (!product) return null;

  const sensitiveCategoryNames = sensitiveCategories.map((cat) => cat.name || cat.label || cat);
  const t = texts || {};
  const { regular, sensitive } = parseCategories(product.category, sensitiveCategoryNames);
  const accepted = getSensitiveCategories(product, language);
  const tagYes = product.ad_label && !['no', ''].includes(String(product.ad_label).toLowerCase());
  const linkBadges = getLinkTypeBadges(product.link_type);
  const website = product.website?.replace(/^https?:\/\//, '').replace(/\/$/, '');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={contentRef} className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="product-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Globe className="h-5 w-5 text-violet-600" />
            {website}
          </DialogTitle>
          <DialogDescription>{es ? 'Información del Sitio' : 'Site information'}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <InfoCell label={t.tableCountry || (es ? 'País' : 'Country')} testId="detail-country">
              <p className="font-semibold flex items-center gap-2">
                {getCountryFlag(product.country) && <img src={getCountryFlag(product.country)} alt={product.country} className="h-4 w-6 object-cover rounded-sm shadow-sm" />}
                {product.country || '-'}
              </p>
            </InfoCell>
            <InfoCell label={t.tableLanguage || (es ? 'Idioma' : 'Language')} testId="detail-language">
              <p className="font-semibold">{product.language || '-'}</p>
            </InfoCell>
            <InfoCell label={es ? 'Tipo Medio' : 'Site type'} testId="detail-site-type">
              <p className="font-semibold">{product.site_type || '-'}</p>
            </InfoCell>
            <InfoCell label={t.tablePrice || (es ? 'Precio' : 'Price')} className="bg-emerald-50" testId="detail-price">
              <p className="font-bold text-emerald-600 text-lg">€{product.pvp || 0}</p>
            </InfoCell>
            <InfoCell label={t.tablePriceSensitive || (es ? 'Precio Sensible' : 'Sensitive price')} className="bg-amber-50" testId="detail-price-sensitive">
              <p className="font-bold text-amber-600 text-lg">{product.premium_sale_price > 0 ? `€${product.premium_sale_price}` : 'NO'}</p>
            </InfoCell>
            <InfoCell label="DR" className="bg-violet-50" testId="detail-dr">
              <p className="font-bold text-violet-600 text-lg">{product.dr ?? '-'}</p>
            </InfoCell>
            <InfoCell label={t.tableTraffic || (es ? 'Tráfico' : 'Traffic')} className="bg-sky-50" testId="detail-traffic">
              <p className="font-bold text-sky-600 text-lg">{(product.organic_traffic || 0).toLocaleString('es-ES')}</p>
            </InfoCell>
            <InfoCell label={t.tableLinkType || (es ? 'Tipo Enlace' : 'Link type')} testId="detail-link-type">
              <div className="flex flex-wrap gap-1">
                {linkBadges.map((b) => <Badge key={b.label} className={`${b.color} font-semibold`}>{b.label}</Badge>)}
                {linkBadges.length === 0 && <p className="font-semibold">-</p>}
              </div>
            </InfoCell>
            <InfoCell label={t.linkDuration || (es ? 'Duración Enlace' : 'Link duration')} testId="detail-duration">
              <p className="font-semibold">{durationLabel(product.link_duration, es)}</p>
            </InfoCell>
            <InfoCell label="Tag" testId="detail-tag">
              <Badge className={tagYes ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}>{tagYes ? (es ? 'Sí' : 'Yes') : 'No'}</Badge>
            </InfoCell>
          </div>

          {regular.length > 0 && (
            <InfoCell label={t.tableCategory || (es ? 'Categoría' : 'Category')} testId="detail-categories">
              <p className="font-semibold text-sm">{regular.join(', ')}</p>
            </InfoCell>
          )}

          {(sensitive.length > 0 || accepted.length > 0) && (
            <div className="bg-gradient-to-r from-amber-50 to-rose-50 rounded-lg p-3 border border-amber-200" data-testid="detail-sensitive">
              <p className="text-xs text-amber-700 font-semibold mb-2 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {es ? 'Acepta Temáticas Sensibles' : 'Accepts Sensitive Topics'}
              </p>
              <div className="flex flex-wrap gap-1">
                {accepted.map((c) => <Badge key={c.name} className={`${c.color} font-medium`}>{c.name}</Badge>)}
                {sensitive.map((c) => <Badge key={c} className="bg-amber-100 text-amber-800 font-normal">{c}</Badge>)}
              </div>
            </div>
          )}

          <SeoMetricsPanel metrics={seo.metrics} history={seo.history} website={website} language={language} />

          <div className="pt-3 border-t">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-violet-600" />
              <h3 className="text-sm font-semibold text-slate-800">{es ? 'Sitios similares' : 'Similar sites'}</h3>
              {!similarLoading && <span className="text-[10px] font-bold bg-violet-100 text-violet-700 rounded-full px-1.5">{similarSites.length}</span>}
              {similarLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-violet-400" />}
            </div>
            {!similarLoading && similarSites.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">{es ? 'No se han encontrado sitios similares.' : 'No similar sites found.'}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="similar-sites-list">
                {similarSites.map((s) => <SimilarSiteCard key={s.id} s={s} matchedOn={firstCat} es={es} onOpen={setCurrent} />)}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3 pt-3 border-t">
            <Button variant="outline" onClick={() => onFindSimilar(product)} className="flex items-center gap-2 border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="find-similar-button">
              <Link2 className="h-4 w-4" />
              {es ? 'Ver todos los similares en el catálogo' : 'See all similar in catalog'}
            </Button>
            {/^https?:\/\//i.test(product.publication_example_url || '') && (
              <Button asChild variant="outline" className="flex items-center gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                <a href={product.publication_example_url} target="_blank" rel="noopener noreferrer nofollow" data-testid="publication-example-button">
                  <FileText className="h-4 w-4" />
                  {es ? 'Ver ejemplo de publicación' : 'See publication example'}
                </a>
              </Button>
            )}
            <Button asChild className="bg-violet-600 hover:bg-violet-700">
              <a href={product.website?.startsWith('http') ? product.website : `https://${product.website}`} target="_blank" rel="noopener noreferrer" data-testid="visit-site-button">
                <ExternalLink className="h-4 w-4 mr-2" />
                {es ? 'Visitar Sitio' : 'Visit Site'}
              </a>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductDetailDialog;
